import { ReactNode } from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { cn } from '@/utils/cn';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, ToastItem } from '@/stores/toastStore';

type ToastVariant = 'success' | 'error' | 'warning' | 'info';

const DEFAULT_DURATION = 5000;

const variantConfig: Record<ToastVariant, { borderColor: string; icon: ReactNode }> = {
  success: {
    borderColor: 'border-l-success',
    icon: <CheckCircle className="w-5 h-5 text-success" />,
  },
  error: {
    borderColor: 'border-l-error',
    icon: <AlertCircle className="w-5 h-5 text-error" />,
  },
  warning: {
    borderColor: 'border-l-warning',
    icon: <AlertTriangle className="w-5 h-5 text-warning" />,
  },
  info: {
    borderColor: 'border-l-info',
    icon: <Info className="w-5 h-5 text-info" />,
  },
};

function ToastItemView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: string) => void }) {
  const config = variantConfig[toast.variant];

  return (
    <ToastPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) onDismiss(toast.id);
      }}
      duration={toast.duration ?? DEFAULT_DURATION}
      className={cn(
        'w-full',
        'bg-surface border border-border rounded-lg shadow-xl',
        'border-l-4',
        config.borderColor,
        'animate-slide-in-right data-[state=closed]:animate-slide-out-right',
        'p-4'
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">{config.icon}</div>
        <div className="flex-1 min-w-0">
          <ToastPrimitive.Title className="text-sm font-semibold text-text-primary">
            {toast.title}
          </ToastPrimitive.Title>
          {toast.description && (
            <ToastPrimitive.Description className="text-sm text-text-secondary mt-1">
              {toast.description}
            </ToastPrimitive.Description>
          )}
        </div>
        {toast.action && (
          <ToastPrimitive.Action asChild altText="Action">
            {toast.action}
          </ToastPrimitive.Action>
        )}
        <ToastPrimitive.Close
          aria-label="关闭"
          className="shrink-0 rounded p-0.5 text-text-muted transition-colors hover:text-text-primary"
        >
          <X size={14} />
        </ToastPrimitive.Close>
      </div>
    </ToastPrimitive.Root>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {children}
      <ToastPrimitive.Viewport className="fixed right-4 top-4 z-50 flex w-full max-w-sm flex-col gap-2 outline-none">
        {toasts.map((toast) => (
          <ToastItemView key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </ToastPrimitive.Viewport>
    </ToastPrimitive.Provider>
  );
}
